import { get } from 'svelte/store';
import { postAttachment } from '../controllers/api/post-attachment';
import type { IAttachedImage } from '../interfaces/attached-image.interface';
import { ConfigStore } from '../store/config';
import {
  state_fileAttachmentErrorKey,
  state_questionImageSearch,
} from '../store/state';
import { getAddFilesConfig } from './features';

const DEFAULT_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
];

let errorTimer: ReturnType<typeof setTimeout> | undefined;

const setAttachmentError = (key: string) => {
  state_fileAttachmentErrorKey.set(key);
  if (errorTimer) clearTimeout(errorTimer);
  errorTimer = setTimeout(() => state_fileAttachmentErrorKey.set(null), 4000);
};

export const handleUploadFiles = (files: FileList) => {
  const filesConfig = getAddFilesConfig(get(ConfigStore));
  const maxFiles = filesConfig?.numberOfFiles ?? 1;
  const currentFiles = get(state_questionImageSearch);
  const remainingSlots = maxFiles - currentFiles.length;

  if (remainingSlots <= 0) {
    setAttachmentError('search_input.error_number_of_files');
    return;
  }

  const filesToAdd = Array.from(files).slice(0, remainingSlots);
  if (files.length > remainingSlots) {
    setAttachmentError('search_input.error_number_of_files');
  }

  const allowedTypes =
    filesConfig?.fileTypes && filesConfig.fileTypes.length
      ? filesConfig.fileTypes
      : DEFAULT_IMAGE_TYPES;
  const maxBytes = (filesConfig?.maxFileSizeMb ?? 10) * 1024 * 1024;

  for (const file of filesToAdd) {
    if (!allowedTypes.includes(file.type)) {
      setAttachmentError('search_input.error_file_type');
      continue;
    }

    if (file.size > maxBytes) {
      setAttachmentError('search_input.error_file_size');
      continue;
    }

    const attachment: IAttachedImage = {
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading',
    };

    state_questionImageSearch.update((items) => [...items, attachment]);

    postAttachment(file)
      .then((response) => {
        state_questionImageSearch.update((items) =>
          items.map((item) =>
            item === attachment
              ? { ...item, id: response.id, status: 'ready' }
              : item
          )
        );
      })
      .catch(() => {
        state_questionImageSearch.update((items) =>
          items.map((item) =>
            item === attachment
              ? {
                  ...item,
                  status: 'error',
                  errorKey: 'search_input.error_file_upload',
                }
              : item
          )
        );
        setAttachmentError('search_input.error_file_upload');
      });
  }
};
